import React, { ReactElement, ReactNode } from 'react';

import cx from 'classnames';
import { GatsbyImage, getImage, getSrc, getSrcSet } from 'gatsby-plugin-image';

import { useButtons } from '../../hooks/data/buttons.hook';
import getRelativeUrl from '../../utils/getRelativeUrl.util';
import { Button, BUTTON_TYPES } from '../Button';
import { IMAGE_LOADING_TYPES } from './Image.const';
import { IImageProps } from './Image.types';

export const Image: React.FC<IImageProps> = ({
	image,
	wrapperClassName,
	imageClassName,
	sizes,
	showWithPlaceholder = true,
	loading = IMAGE_LOADING_TYPES.Lazy,
	objectFit = 'cover',
}: IImageProps): ReactElement => {
	const { buttonDownload } = useButtons();

	const { canDownload, imageFile, title, url, hasFocalPoint, focalPoint } = image;
	const img = getImage(imageFile);

	let style = {};

	if (objectFit === 'cover') {
		style = {
			objectFit: 'cover',
			objectPosition:
				hasFocalPoint && focalPoint
					? `${focalPoint[0] * 100}% ${focalPoint[1] * 100}%`
					: 'top center',
		};
	}

	const handleDownload = async (): Promise<void> => {
		const response = await fetch(getRelativeUrl(image.url));
		const blob = await response.blob();

		const blobUrl = window.URL.createObjectURL(blob);
		const $a = document.createElement('a');
		$a.download = url.replace(/^.*[\\/]/, '');
		$a.href = blobUrl;
		document.body.appendChild($a);
		$a.click();
		$a.remove();
	};

	const renderImage = (): ReactNode => {
		// Gatsby image
		if (showWithPlaceholder && img) {
			return (
				<GatsbyImage
					className={cx('c-image', imageClassName)}
					image={img}
					alt={title || ''}
					loading={loading}
					objectFit={objectFit}
					style={style}
				/>
			);
		}

		// Regular image + fallback in case gatsby can't get the image
		const src = img ? getSrc(img) : getRelativeUrl(url);
		const srcSet = img ? getSrcSet(img) : '';

		return (
			<img
				className={cx('c-image', imageClassName)}
				src={src}
				srcSet={srcSet}
				alt={title || ''}
				sizes={sizes || '100vw'}
				style={style}
			/>
		);
	};

	return (
		<div className={cx('c-image__wrapper', wrapperClassName)}>
			{renderImage()}

			{canDownload && url && (
				<Button
					className="c-image__button"
					type={BUTTON_TYPES.SMALL}
					label={buttonDownload.label}
					accessibilityLabel={buttonDownload.accessibilityLabel}
					onClickButton={handleDownload}
				/>
			)}
		</div>
	);
};
